import { useMemo, useCallback, useState, useEffect } from 'react';
import { PREPROCESSED_ANALYTICS } from '../../../../types/analytics';
import { IPreprocessedAnalyticsPayload } from '../../../../types/chart';
import { useGetPreprocessedAnalyticsQuery } from '../../../../queries/useGetPreprocessedAnalyticsQuery';
import { QUERY_KEYS } from '../../../../queries/queryKeys';
import Api from '../../../../api/Api';
import { useQuery } from '@tanstack/react-query';
import { useSignalR } from '../../../../utils/hooks';

interface UsePreprocessedReportsParams {
  coordinates: number[];
  defaultDataset: any;
}

export const usePreprocessedParams = ({
  coordinates,
  defaultDataset,
}: UsePreprocessedReportsParams) => {
  const [signalRErrorMessage, setSignalRErrorMessage] = useState<string>('');
  const [showSignalRError, setShowSignalRError] = useState<boolean>(false);
  const [datasetSW, setDatasetSW] = useState<string>('');
  const [datasetHD, setDatasetHD] = useState<string>('');
  const [datasetWind, setDatasetWind] = useState<string>('');
  const { messageReceived, receivedMessageText, resetMessageState } =
    useSignalR('AnalyticsReceiveMessage', 'JobResult');

  const handleDatasetChange = useCallback(
    (type: 'SW' | 'HD' | 'Wind') => (value: string) => {
      const actualValue = value === 'none' ? '' : value;
      if (type === 'SW') setDatasetSW(actualValue);
      if (type === 'HD') setDatasetHD(actualValue);
      if (type === 'Wind') setDatasetWind(actualValue);
    },
    []
  );

  useEffect(() => {
    if (messageReceived) {
      const splitMessage = receivedMessageText.split(':');
      let hubMessage: string = '';

      if (splitMessage.length > 1) {
        hubMessage = splitMessage[splitMessage.length - 1];
      } else {
        hubMessage = receivedMessageText;
      }

      if (hubMessage && hubMessage.toLowerCase().trim() !== 'success') {
        setSignalRErrorMessage(hubMessage);
        setShowSignalRError(true);
      } else {
        setShowSignalRError(false);
      }

      resetMessageState();
    } else {
      setShowSignalRError(false);
    }
  }, [messageReceived, receivedMessageText, resetMessageState]);

  const findId = (key: string) => {
    const found = defaultDataset.find((item: any) => item.key === key);
    return found ? found.value : '';
  };

  const {
    datasetSW: defaultDatasetSW,
    datasetHD: defaultDatasetHD,
    datasetWind: defaultDatasetWind,
  } = useMemo(() => {
    return {
      datasetSW: findId('SW'),
      datasetHD: findId('HD'),
      datasetWind: findId('Wind'),
    };
  }, [defaultDataset]);

  useEffect(() => {
    if (defaultDatasetSW) {
      setDatasetSW(defaultDatasetSW);
    }
    if (defaultDatasetHD) {
      setDatasetHD(defaultDatasetHD);
    }
    if (defaultDatasetWind) {
      setDatasetWind(defaultDatasetWind);
    }
  }, [defaultDatasetSW, defaultDatasetHD, defaultDatasetWind]);

  const checkIncludesPreprocessed = useCallback((metadata: any) => {
    if (!metadata?.AnalyticsDefinition) return false;
    const analyticDefs = metadata.AnalyticsDefinition.split('|');
    return analyticDefs.some(
      (analytic: string) =>
        analytic === 'XLS' || analytic === PREPROCESSED_ANALYTICS.XLS
    );
  }, []);

  const { data: metaSW } = useQuery({
    queryKey: [QUERY_KEYS.dataset.metadata, datasetSW],
    queryFn: async () =>
      datasetSW ? await Api.metadata.getMetadata(datasetSW) : null,
    enabled: !!datasetSW,
  });

  const { data: metaHD } = useQuery({
    queryKey: [QUERY_KEYS.dataset.metadata, datasetHD],
    queryFn: async () =>
      datasetHD ? await Api.metadata.getMetadata(datasetHD) : null,
    enabled: !!datasetHD,
  });

  const { data: metaWind } = useQuery({
    queryKey: [QUERY_KEYS.dataset.metadata, datasetWind],
    queryFn: async () =>
      datasetWind ? await Api.metadata.getMetadata(datasetWind) : null,
    enabled: !!datasetWind,
  });

  const hasPreprocessedSW = useMemo(
    () => checkIncludesPreprocessed(metaSW),
    [metaSW, checkIncludesPreprocessed]
  );
  const hasPreprocessedHD = useMemo(
    () => checkIncludesPreprocessed(metaHD),
    [metaHD, checkIncludesPreprocessed]
  );
  const hasPreprocessedWind = useMemo(
    () => checkIncludesPreprocessed(metaWind),
    [metaWind, checkIncludesPreprocessed]
  );

  const { data: waterDepthSW } = useQuery({
    queryKey: [QUERY_KEYS.dataset.waterDepth, coordinates, datasetSW],
    queryFn: async () =>
      Api.detail.getWaterDepthAndCell(coordinates, datasetSW),
    enabled: !!datasetSW && !!coordinates && hasPreprocessedSW,
  });

  const { data: waterDepthHD } = useQuery({
    queryKey: [QUERY_KEYS.dataset.waterDepth, coordinates, datasetHD],
    queryFn: async () =>
      Api.detail.getWaterDepthAndCell(coordinates, datasetHD),
    enabled: !!datasetHD && !!coordinates && hasPreprocessedHD,
  });

  const { data: waterDepthWind } = useQuery({
    queryKey: [QUERY_KEYS.dataset.waterDepth, coordinates, datasetWind],
    queryFn: async () =>
      Api.detail.getWaterDepthAndCell(coordinates, datasetWind),
    enabled: !!datasetWind && !!coordinates && hasPreprocessedWind,
  });

  const createPayload = (datasetId: string, depthData: any) => ({
    Latitude: `${coordinates[1]}`,
    Longitude: `${coordinates[0]}`,
    DatasetId: datasetId,
    WaterDepth: `${depthData?.[0]}`,
  });

  const payloadSW: IPreprocessedAnalyticsPayload = useMemo(
    () => createPayload(datasetSW, waterDepthSW),
    [datasetSW, coordinates, waterDepthSW]
  );
  const payloadHD: IPreprocessedAnalyticsPayload = useMemo(
    () => createPayload(datasetHD, waterDepthHD),
    [datasetHD, coordinates, waterDepthHD]
  );
  const payloadWind: IPreprocessedAnalyticsPayload = useMemo(
    () => createPayload(datasetWind, waterDepthWind),
    [datasetWind, coordinates, waterDepthWind]
  );

  const querySW = useGetPreprocessedAnalyticsQuery(
    payloadSW,
    !!waterDepthSW && hasPreprocessedSW,
    coordinates
  );
  const queryHD = useGetPreprocessedAnalyticsQuery(
    payloadHD,
    !!waterDepthHD && hasPreprocessedHD,
    coordinates
  );
  const queryWind = useGetPreprocessedAnalyticsQuery(
    payloadWind,
    !!waterDepthWind && hasPreprocessedWind,
    coordinates
  );

  return {
    hasPreprocessedSW,
    hasPreprocessedHD,
    hasPreprocessedWind,

    datasetSW,
    datasetHD,
    datasetWind,

    dataSW: querySW.data,
    dataHD: queryHD.data,
    dataWind: queryWind.data,

    isLoadingSW: querySW.isLoading,
    isLoadingHD: queryHD.isLoading,
    isLoadingWind: queryWind.isLoading,

    errorSW: querySW.error,
    errorHD: queryHD.error,
    errorWind: queryWind.error,

    handleDatasetChange,
    signalRErrorMessage: signalRErrorMessage,
    showSignalRError: showSignalRError,
  };
};
